// ============================================================================
// STUDIO BOOKING FORM — request an advance booking for a Co-Creation Studio room
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarPlus, Building2, User, Tag, Target, Loader, AlertCircle, ChevronLeft, Clock, ChevronRight, Check, Users
} from 'lucide-react';
import {
  createBooking,
  listEventTypes,
  listFacilitators,
  EventType,
  FacilitatorSummary,
  BookingDateType,
  DATE_TYPE_LABELS,
  TIER_LABELS,
  CreateBookingInput,
  ConflictInfo
} from '../services/studio-bookings';
import { listAllRoomsWithStatus, RoomWithAvailability } from '../services/rooms';

type TabStep = 0 | 1 | 2;

const FACILITATOR_TBC = '__TBC__';
const FACILITATOR_NA  = '__NA__';

const DATE_SLOT_TIMES: Record<BookingDateType, string> = {
  morning:   '09:00–12:30',
  afternoon: '13:30–17:00',
  full_day:  '09:00–17:00',
  multi_day: '09:00–17:00'
};

const DATE_SLOT_LABELS: Record<BookingDateType, string> = {
  morning:   'Morning',
  afternoon: 'Afternoon',
  full_day:  'Full Day',
  multi_day: 'Multi-day'
};

export default function StudioBookingForm() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<RoomWithAvailability[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [facilitators, setFacilitators] = useState<FacilitatorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [tab, setTab] = useState<TabStep>(0);

  const [roomId, setRoomId] = useState('');
  const [facilitatorId, setFacilitatorId] = useState('');
  const [customer, setCustomer] = useState('');
  const [eventTypeId, setEventTypeId] = useState('');
  const [customerStatus, setCustomerStatus] = useState<'existing' | 'new' | 'internal'>('existing');
  const [billingType, setBillingType] = useState<'investment' | 'chargeable' | 'na'>('chargeable');
  const [dateType, setDateType] = useState<BookingDateType>('full_day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [goal, setGoal] = useState('');
  const [numberOfCustomers, setNumberOfCustomers] = useState<string>('');
  const [numberOfAttendees, setNumberOfAttendees] = useState<string>('');

  useEffect(() => {
    Promise.all([listAllRoomsWithStatus(), listEventTypes(), listFacilitators()])
      .then(([r, e, f]) => { setRooms(r); setEventTypes(e); setFacilitators(f); })
      .catch(() => setError('Failed to load form data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (dateType !== 'multi_day') setEndDate(startDate);
  }, [dateType, startDate]);

  const selectedFacilitator = facilitators.find(f => f.userId === facilitatorId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setConflicts([]);
    setSubmitting(true);
    try {
      const input: CreateBookingInput = {
        roomId,
        facilitatorId,
        facilitatorName: facilitatorId === FACILITATOR_TBC ? 'TBC' : facilitatorId === FACILITATOR_NA ? 'N/A' : selectedFacilitator?.displayName || '',
        facilitatorTier: selectedFacilitator?.tier || 'tier2',
        customer,
        eventTypeId,
        isNewCustomer: customerStatus === 'new',
        customerStatus,
        billingType,
        dateType,
        startDate,
        endDate: dateType !== 'multi_day' ? startDate : endDate,
        goal,
        ...(numberOfCustomers !== '' && { numberOfCustomers: Number(numberOfCustomers) }),
        ...(numberOfAttendees !== '' && { numberOfAttendees: Number(numberOfAttendees) })
      };
      await createBooking(input);
      navigate('/studio/bookings', { state: { flash: 'Booking submitted — pending Tier 1 review.' } });
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.conflicts) {
        setConflicts(err.response.data.conflicts);
        setError(err.response.data.error);
      } else {
        setError(err.response?.data?.error || 'Failed to submit booking');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const TABS = ['Schedule', 'People', 'Details'] as const;

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto p-8 font-display flex items-center justify-center">
        <Loader className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <button onClick={() => navigate('/studio/bookings')} className="hover:text-primary transition-colors flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> My Bookings
          </button>
        </div>

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <CalendarPlus size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Book a Session</h1>
              <p className="text-slate-500 text-sm font-medium">Request a Co-Creation Studio room — pending Tier 1 review</p>
            </div>
          </div>
        </header>

        {/* Form card — step indicator + content + navigation */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">

          {/* Step indicator */}
          <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center gap-0">
            {TABS.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i < tab ? 'bg-primary text-white' : i === tab ? 'bg-navy text-white' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {i < tab ? <Check size={13} /> : i + 1}
                  </div>
                  <span className={`text-sm font-medium transition-colors ${
                    i === tab ? 'text-navy' : i < tab ? 'text-primary' : 'text-slate-400'
                  }`}>{label}</span>
                </div>
                {i < TABS.length - 1 && (
                  <div className={`flex-1 h-px mx-3 transition-colors ${i < tab ? 'bg-primary' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700">{error}</p>
                {conflicts.length > 0 && (
                  <ul className="mt-2 text-sm text-red-600 space-y-1">
                    {conflicts.map(c => (
                      <li key={c.bookingId}>
                        {c.customer} — {DATE_TYPE_LABELS[c.dateType]} on {c.startDate}
                        {c.startDate !== c.endDate ? ` to ${c.endDate}` : ''} ({c.status})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Tab 0 — Schedule */}
            {tab === 0 && (
              <>
                {/* Room */}
                <div className="p-5 border-b border-[#e2e8f0]">
                  <p className="text-sm font-semibold text-navy mb-3 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-slate-400" />Room
                  </p>
                  <div className="flex flex-col gap-2">
                    {rooms.length === 0 ? (
                      <p className="text-sm text-slate-400">No rooms configured.</p>
                    ) : rooms.map(r => (
                      <button
                        key={r.roomId}
                        type="button"
                        onClick={() => setRoomId(r.roomId)}
                        className={`flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                          roomId === r.roomId
                            ? 'border-primary bg-primary/5'
                            : 'border-[#e2e8f0] hover:border-primary/40 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            roomId === r.roomId ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'
                          }`}>
                            <Building2 size={14} />
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${roomId === r.roomId ? 'text-navy' : 'text-slate-700'}`}>
                              {r.name}
                            </p>
                            {r.description && (
                              <p className="text-xs text-slate-400 mt-0.5">{r.description}</p>
                            )}
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          r.available
                            ? 'bg-green-50 text-green-600 border border-green-200'
                            : 'bg-red-50 text-red-500 border border-red-200'
                        }`}>
                          {r.available ? 'Available' : 'In use'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* When */}
                <div className="p-5">
                  <p className="text-sm font-semibold text-navy mb-3 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-slate-400" />When
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {(['morning', 'afternoon', 'full_day', 'multi_day'] as BookingDateType[]).map(dt => (
                      <button
                        key={dt}
                        type="button"
                        onClick={() => setDateType(dt)}
                        className={`px-2 py-2.5 rounded-xl border transition-all flex flex-col items-center gap-0.5 ${
                          dateType === dt
                            ? 'bg-primary text-white border-primary shadow-sm'
                            : 'bg-white text-slate-500 border-[#e2e8f0] hover:border-primary/50 hover:text-navy'
                        }`}
                      >
                        <span className="text-xs font-semibold">{DATE_SLOT_LABELS[dt]}</span>
                        <span className={`text-[10px] font-normal leading-tight ${
                          dateType === dt ? 'text-white/80' : 'text-slate-400'
                        }`}>{DATE_SLOT_TIMES[dt]}</span>
                      </button>
                    ))}
                  </div>
                  <div className={`grid gap-3 ${dateType === 'multi_day' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        {dateType === 'multi_day' ? 'Start date' : 'Date'}
                      </label>
                      <input
                        type="date"
                        required
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      />
                    </div>
                    {dateType === 'multi_day' && (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">End date</label>
                        <input
                          type="date"
                          required
                          min={startDate || undefined}
                          value={endDate}
                          onChange={e => setEndDate(e.target.value)}
                          className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                        />
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-400">Weekdays only — weekend requests subject to approval.</p>
                </div>
              </>
            )}

            {/* Tab 1 — People */}
            {tab === 1 && (
              <>
                <div className="p-5 border-b border-[#e2e8f0]">
                  <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                    <User className="w-4 h-4 text-slate-400" />Facilitator
                  </label>
                  <select
                    required
                    value={facilitatorId}
                    onChange={e => setFacilitatorId(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                  >
                    <option value="">Select a facilitator…</option>
                    <option value={FACILITATOR_TBC}>TBC — to be confirmed</option>
                    <option value={FACILITATOR_NA}>N/A — no facilitator needed</option>
                    {facilitators.map(f => (
                      <option key={f.userId} value={f.userId}>
                        {f.displayName} — {TIER_LABELS[f.tier]}
                      </option>
                    ))}
                  </select>
                  {selectedFacilitator && (
                    <p className="mt-1.5 text-xs text-slate-500">{selectedFacilitator.email}</p>
                  )}
                </div>
                <div className="p-5 border-b border-[#e2e8f0]">
                  <label className="block text-sm font-semibold text-navy mb-2">Customer / Organisation</label>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    placeholder="e.g. Acme Corp"
                    value={customer}
                    onChange={e => setCustomer(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                </div>
                <div className="p-5 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-slate-400" />No. of Customers
                    </label>
                    <input
                      type="number"
                      min={0}
                      placeholder="e.g. 3"
                      value={numberOfCustomers}
                      onChange={e => setNumberOfCustomers(e.target.value)}
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-slate-400" />No. of Attendees
                    </label>
                    <input
                      type="number"
                      min={0}
                      placeholder="e.g. 8"
                      value={numberOfAttendees}
                      onChange={e => setNumberOfAttendees(e.target.value)}
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Tab 2 — Details */}
            {tab === 2 && (
              <>
                <div className="p-5 border-b border-[#e2e8f0]">
                  <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                    <Tag className="w-4 h-4 text-slate-400" />Event Type
                  </label>
                  <select
                    required
                    value={eventTypeId}
                    onChange={e => setEventTypeId(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                  >
                    <option value="">Select event type…</option>
                    {eventTypes.map(et => (
                      <option key={et.eventTypeId} value={et.eventTypeId}>{et.name}</option>
                    ))}
                  </select>
                </div>
                <div className="p-5 grid grid-cols-2 gap-6 border-b border-[#e2e8f0]">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Customer Status</label>
                    <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                      {(['existing', 'new', 'internal'] as const).map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setCustomerStatus(s)}
                          className={`flex-1 py-2 text-xs font-semibold transition-all ${
                            customerStatus === s ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                          }`}
                        >
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Billing</label>
                    <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                      {([['chargeable', 'Chargeable'], ['investment', 'Investment'], ['na', 'N/A']] as const).map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setBillingType(val)}
                          className={`flex-1 py-2 text-xs font-semibold transition-all ${
                            billingType === val ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <label className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                    <Target className="w-4 h-4 text-slate-400" />Goal / Reason for session
                  </label>
                  <textarea
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="What are you hoping to achieve in this session?"
                    value={goal}
                    onChange={e => setGoal(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                  />
                  <p className="mt-1 text-xs text-slate-400 text-right">{goal.length}/500</p>
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
              <button
                type="button"
                onClick={() => setTab((tab - 1) as TabStep)}
                disabled={tab === 0}
                className="flex items-center gap-1.5 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-0 disabled:pointer-events-none"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              {tab < 2 ? (
                <button
                  type="button"
                  onClick={() => setTab((tab + 1) as TabStep)}
                  className="flex items-center gap-1.5 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark text-white text-sm font-bold disabled:opacity-60 transition-colors shadow-sm"
                >
                  {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                  {submitting ? 'Submitting…' : 'Submit Booking'}
                </button>
              )}
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}
